import React from 'react';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isValoradorEnabled } from '../../util/valoradorEnvironment';
import { createSlug } from '../../util/urlHelpers';
import { useHideOnScroll } from '../../util/useHideOnScroll';
import { PROVINCIA_LABELS, SECTOR_LABELS, FACTURACION_LABELS } from '../../util/listingFieldLabels';
import { MANDATO_LISTING_TYPE } from '../../config/configListing';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { LayoutComposer, NamedLink } from '../../components';

import StaticPage from '../PageBuilder/StaticPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { HOME_OPPORTUNITIES } from './homeOpportunities';
import css from './LandingPage.module.css';

const Message = ({ name }) => <FormattedMessage id={`HomeConcept.${name}`} />;
const Arrow = () => <span aria-hidden="true">↗</span>;
const Check = () => (
  <svg className={css.check} width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const ExploreLink = () => (
  <NamedLink name="ExplorePage" className={css.primaryButton}>
    <Message name="explore" />
    <span aria-hidden="true">→</span>
  </NamedLink>
);
const PublishLink = () => (
  <NamedLink name="NewListingPage" className={css.outlineButton}>
    <Message name="publish" />
  </NamedLink>
);

const Hero = () => (
  <section className={css.hero} aria-labelledby="home-title">
    <div className={css.wrap}>
      <h1 id="home-title" className={css.heroTitle}>
        <Message name="title" />
      </h1>
      <p className={css.heroSubtitle}>
        <Message name="subtitle" />
      </p>
      <ExploreLink />
      <ul className={css.assurances}>
        {['free', 'anonymous', 'direct'].map(name => (
          <li key={name} className={css.assurance}>
            <Check />
            <Message name={name} />
          </li>
        ))}
      </ul>
      <div className={css.heroPublish}>
        <p className={css.publishPrompt}>
          <Message name="publishPrompt" />
        </p>
        <PublishLink />
      </div>
    </div>
  </section>
);

/** Compact real listing preview; buyers keep their generated title instead of invented budgets. */
const Opportunity = ({ category, entry }) => {
  const intl = useIntl();
  const { key, listingType } = category;
  const listing = entry?.listing;
  const pending = !entry || entry.loading;
  const error = entry?.error;
  const buyer = listingType === MANDATO_LISTING_TYPE;
  const { title, publicData = {} } = listing?.attributes || {};
  const saleContext = [PROVINCIA_LABELS[publicData.provincia], SECTOR_LABELS[publicData.sector]]
    .filter(Boolean)
    .join(' · ');
  const revenue = FACTURACION_LABELS[publicData.facturacion];
  const badge = (
    <span className={`${css.tag} ${css[key]}`}>
      <Message name={`${key}.tag`} />
    </span>
  );
  if (listing) {
    return (
      <NamedLink
        className={css.opportunity}
        name="ListingPage"
        params={{ id: listing.id.uuid, slug: createSlug(title) }}
        ariaLabel={title}
      >
        {badge}
        <h3 className={css.cardTitle}>{title}</h3>
        {!buyer && saleContext ? <p className={css.cardMeta}>{saleContext}</p> : null}
        {!buyer && revenue ? (
          <p className={css.cardMeta}>
            <Message name="revenue" /> <strong className={css.revenue}>{revenue}</strong>
          </p>
        ) : null}
        <span className={css.cardAction}>
          <Message name="view" />
          <Arrow />
        </span>
      </NamedLink>
    );
  }
  return (
    <article className={`${css.opportunity} ${css.emptyCard}`} aria-busy={pending}>
      {badge}
      <h3 className={css.cardTitle}>
        <Message name={pending ? 'loading' : error ? 'loadError' : `${key}.empty`} />
      </h3>
      <p className={css.cardMeta}>
        <Message name={pending ? 'loadingText' : error ? 'errorText' : `${key}.invitation`} />
      </p>
      {!pending &&
        (error ? (
          <NamedLink
            name={buyer ? 'SearchPageCompradores' : 'SearchPage'}
            className={css.cardAction}
          >
            <Message name="browse" />
            <Arrow />
          </NamedLink>
        ) : (
          <NamedLink
            name="NewListingPage"
            to={{ search: `?listingType=${listingType}` }}
            className={css.cardAction}
            ariaLabel={intl.formatMessage({ id: `HomeConcept.${key}.invitation` })}
          >
            <Message name="publish" />
            <Arrow />
          </NamedLink>
        ))}
    </article>
  );
};

const Recent = ({ recentByCategory }) => (
  <section id="oportunidades" tabIndex={-1} className={css.recent} aria-labelledby="recent-title">
    <div className={css.wrap}>
      <div className={css.sectionHead}>
        <div>
          <h2 id="recent-title" className={css.sectionTitle}>
            <Message name="recentTitle" />
          </h2>
          <p className={css.sectionLead}>
            <span className={css.liveDot} aria-hidden="true" />
            <Message name="recentSubtitle" />
          </p>
        </div>
        <div className={css.browseLinks}>
          <NamedLink name="ExplorePage" className={css.textLink}>
            <Message name="exploreMore" />
            <Arrow />
          </NamedLink>
        </div>
      </div>
      <div className={css.cards}>
        {HOME_OPPORTUNITIES.map(category => (
          <Opportunity
            key={category.key}
            category={category}
            entry={recentByCategory[category.key]}
          />
        ))}
      </div>
    </div>
  </section>
);

const Market = () => (
  <section className={css.market} aria-labelledby="market-title">
    <div className={css.wrap}>
      <h2 id="market-title" className={css.centerTitle}>
        <Message name="marketTitle" />
      </h2>
      <p className={css.centerLead}>
        <Message name="marketText" />
      </p>
      <div className={css.modes}>
        {HOME_OPPORTUNITIES.map(({ key }) => (
          <div className={css.mode} key={key}>
            <h3 className={css.modeTitle}>
              <span className={`${css.modeDot} ${css[key]}`} aria-hidden="true" />
              <Message name={`${key}.mode`} />
            </h3>
            <p className={css.modeText}>
              <Message name={`${key}.text`} />
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const How = () => (
  <section className={css.how} aria-labelledby="how-title">
    <div className={css.wrap}>
      <h2 id="how-title" className={css.centerTitle}>
        <Message name="howTitle" />
      </h2>
      <p className={css.centerLead}>
        <Message name="howSubtitle" />
      </p>
      <ol className={css.steps}>
        {['explore', 'connect', 'decide'].map((key, index) => (
          <li className={css.step} key={key}>
            <div className={css.stepHeading}>
              <span className={css.stepNumber} aria-hidden="true">
                {index + 1}
              </span>
              <h3 className={css.stepTitle}>
                <Message name={`step.${key}.title`} />
              </h3>
            </div>
            <p className={css.stepText}>
              <Message name={`step.${key}.text`} />
            </p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Why = () => (
  <section className={css.why} aria-labelledby="why-title">
    <div className={css.wrap}>
      <h2 id="why-title" className={css.centerTitle}>
        <Message name="whyTitle" />
      </h2>
      <div className={css.benefits}>
        {['free', 'anonymous', 'contact', 'connect'].map(key => (
          <div className={css.benefit} key={key}>
            <span className={css.benefitIcon}>
              <Check />
            </span>
            <div>
              <h3 className={css.benefitTitle}>
                <Message name={`why.${key}.title`} />
              </h3>
              <p className={css.modeText}>
                <Message name={`why.${key}.text`} />
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Valuation = () => (
  <section className={css.valuation} aria-labelledby="valuation-title">
    <div className={css.wrap}>
      <div className={css.valuationGrid}>
        <div>
          <p className={css.valuationEyebrow}>
            <Message name="valuation.label" />
          </p>
          <h2 id="valuation-title" className={css.valuationTitle}>
            <Message name="valuation.title" />
          </h2>
          <p className={css.valuationIntro}>
            <Message name="valuation.intro" />
          </p>
          <NamedLink name="ValoradorPrototypePage" className={css.primaryButton}>
            <Message name="valuation.cta" />
            <Arrow />
          </NamedLink>
          <p className={css.valuationNote}>
            <Message name="valuation.note" />
          </p>
        </div>
        <div className={css.valuationPreview}>
          <div className={css.valuationPreviewHeader}>
            <span>
              <Message name="valuation.previewTitle" />
            </span>
            <span className={css.valuationExample}>
              <Message name="valuation.example" />
            </span>
          </div>
          <p className={css.valuationPreviewLabel}>
            <Message name="valuation.valueLabel" />
          </p>
          <p className={css.valuationAmount}>
            <Message name="valuation.exampleRange" />
          </p>
          <p className={css.valuationCaption}>
            <Message name="valuation.caption" />
          </p>
          <div className={css.valuationRange} aria-hidden="true">
            <span className={css.valuationRangeBand} />
          </div>
          <ul className={css.valuationFeatures}>
            {['assets', 'private'].map(key => (
              <li key={key} className={css.valuationFeature}>
                <Message name={`valuation.${key}`} />
                <Check />
              </li>
            ))}
          </ul>
          <p className={css.valuationPreviewNote}>
            <Message name="valuation.publish" />
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Closing = () => (
  <section className={css.closing} aria-labelledby="closing-title">
    <div className={css.wrap}>
      <div className={css.closingPanel}>
        <h2 id="closing-title" className={css.closingTitle}>
          <Message name="closingTitle" />
        </h2>
        <div className={css.closingActions}>
          <ExploreLink />
          <PublishLink />
        </div>
        <p className={css.closingNote}>
          <Message name="closingNote" />
        </p>
      </div>
    </div>
  </section>
);

/** Alfredo's home structure, with the existing marketplace shell and corporate palette. */
export const LandingPageComponent = ({ recentByCategory = {} }) => {
  const intl = useIntl();
  const headerHidden = useHideOnScroll();
  return (
    <StaticPage
      title={intl.formatMessage({ id: 'LandingPage.pageTitle' })}
      schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Uroborox',
        description: intl.formatMessage({ id: 'LandingPage.pageDescription' }),
      }}
    >
      <LayoutComposer areas={'topbar\nmain\nfooter'} className={css.layout}>
        {({ Topbar, Main, Footer }) => (
          <>
            <Topbar as="header" className={`${css.topbar} ${headerHidden ? css.topbarHidden : ''}`}>
              <TopbarContainer currentPage="LandingPage" />
            </Topbar>
            <Main as="main" id="main-content" className={css.main}>
              <Hero />
              <Recent recentByCategory={recentByCategory} />
              <Market />
              <How />
              <Why />
              {isValoradorEnabled() ? <Valuation /> : null}
              <Closing />
            </Main>
            <Footer>
              <FooterContainer />
            </Footer>
          </>
        )}
      </LayoutComposer>
    </StaticPage>
  );
};

const mapStateToProps = state => ({
  recentByCategory: Object.fromEntries(
    Object.entries(state.LandingPage.recentByCategory || {}).map(([key, entry]) => [
      key,
      { ...entry, listing: entry.id ? getListingsById(state, [entry.id])[0] : null },
    ])
  ),
});

export default connect(mapStateToProps)(LandingPageComponent);
